// The echo a table row casts onto its own column or bar. Highcharts' own point states cannot draw
// it: `halo` is a pie/marker affordance, and a `states.hover` border would trace all four sides. The
// echo has to stop dead at the axis -- top and both sides on a column, top/right/bottom on a bar --
// so the shape is drawn as a path with only its outer corners rounded.

import Highcharts from 'highcharts'

const SPREAD = 5
// rounder than the column's own 10, so the outline reads as a wrapper rather than a thick border
const RADIUS = 16
const CLASS = 'aytm-point-halo'
// chart types that mark the point themselves rather than being outlined; a scatter marker draws its own halo
const SELF_MARKING = ['pie', 'sunburst', 'treemap', 'scatter']

// rounded on the two corners away from the axis, square where it meets it
const columnPath = (x, y, w, h, r) => [
  'M',
  x,
  y + h,
  'L',
  x,
  y + r,
  'Q',
  x,
  y,
  x + r,
  y,
  'L',
  x + w - r,
  y,
  'Q',
  x + w,
  y,
  x + w,
  y + r,
  'L',
  x + w,
  y + h,
  'Z'
]

const barPath = (x, y, w, h, r) => [
  'M',
  x,
  y,
  'L',
  x + w - r,
  y,
  'Q',
  x + w,
  y,
  x + w,
  y + r,
  'L',
  x + w,
  y + h - r,
  'Q',
  x + w,
  y + h,
  x + w - r,
  y + h,
  'L',
  x,
  y + h,
  'Z'
]

// A stacked segment answers to neither axis: it sits between its neighbours, so nothing squares it
// off and the outline closes all the way round.
const segmentPath = (x, y, w, h, r) => [
  'M',
  x,
  y + r,
  'Q',
  x,
  y,
  x + r,
  y,
  'L',
  x + w - r,
  y,
  'Q',
  x + w,
  y,
  x + w,
  y + r,
  'L',
  x + w,
  y + h - r,
  'Q',
  x + w,
  y + h,
  x + w - r,
  y + h,
  'L',
  x + r,
  y + h,
  'Q',
  x,
  y + h,
  x,
  y + h - r,
  'Z'
]

// Stacked on top of something rather than standing on the axis. Highcharts totals a stack into
// `stackY`, so a segment carrying the whole total is the one at the bottom -- which covers the
// single-series column charts too, where the ingot's stacking leaves every point its own stack.
const isMidStack = point => null != point.stackY && point.stackY !== point.y

const pointOf = (chart, id) => {
  for (const series of chart.series || []) {
    if (false === series.visible) continue
    for (const point of series.points || []) {
      if (point.id === id) return point
    }
  }
  return null
}

export const clearHalo = chart => {
  if (chart?.aytmHalo) {
    chart.aytmHalo.destroy()
    chart.aytmHalo = null
  }
  if (chart?.aytmHaloEdge) {
    chart.aytmHaloEdge.graphic?.attr({ stroke: chart.aytmHaloEdge.stroke })
    chart.aytmHaloEdge = null
  }
  if (chart?.aytmHaloState) {
    chart.aytmHaloState.setState('')
    chart.aytmHaloState = null
  }
}

// `id` is the answer key shared with the table (`a<position>`); null clears.
export const drawHalo = (chart, id) => {
  if (!chart?.renderer) return
  clearHalo(chart)
  if (null == id) return

  const point = pointOf(chart, id)
  if (!point) return

  // A pie draws its own highlight on hover; the table asks for that rather than adding a second one.
  // The tree charts answer the same way: a node is nested inside its parent rather than standing on
  // an axis, so an outline hugging it would trace the parent's fill and read as marking both.
  if (SELF_MARKING.includes(point.series?.type)) {
    point.setState('hover')
    chart.aytmHaloState = point
    return
  }
  if (!point.shapeArgs) return

  // Measured off the rendered shape rather than computed from the point: a bar chart is a rotated
  // column chart, and reading its geometry straight from the point lands the outline in the wrong
  // place. The drawn box is orientation-agnostic; only which end gets the rounding differs.
  const shapeBox = point.graphic?.element?.getBoundingClientRect()
  const containerBox = chart.container?.getBoundingClientRect()
  if (!shapeBox || !containerBox) return

  const x = shapeBox.left - containerBox.left
  const y = shapeBox.top - containerBox.top
  const w = shapeBox.width
  const h = shapeBox.height
  const r = Math.min(RADIUS, w / 2 + SPREAD, h / 2 + SPREAD)

  const d = isMidStack(point)
    ? segmentPath(x - SPREAD, y - SPREAD, w + SPREAD * 2, h + SPREAD * 2, r)
    : chart.inverted
      ? barPath(x, y - SPREAD, w + SPREAD, h + SPREAD * 2, r)
      : columnPath(x - SPREAD, y - SPREAD, w + SPREAD * 2, h + SPREAD, r)

  chart.aytmHalo = chart.renderer
    .path(d) // above the gridlines (1), below the columns (3), so the plot does not show through it
    .attr({ class: CLASS, fill: point.color, opacity: 0.3, zIndex: 2 })
    .add()

  // columns carry a hairline border in the plot's own background colour, which reads as a gap
  // between the fill and the outline hugging it -- while the outline is up the column's border
  // takes its own fill instead
  if (point.graphic) {
    chart.aytmHaloEdge = { graphic: point.graphic, stroke: point.graphic.attr('stroke') }
    point.graphic.attr({ stroke: point.color })
  }
}

// A block's chart and its table share a BlockWrapper, whoever mounted them -- the chart comes from
// React, the answers table is often mounted by the legacy view. Walking the DOM finds the pair
// without either side having to know about the other.
//
// A block that draws a list of sub-items -- a matrix batch, an extended open end's variant -- holds
// a chart and a table per item inside that one wrapper, so the pairing is the nearest sub-item and
// only the block where there is none. Listed nearest-first is immaterial: `closest` answers with the
// nearest ancestor matching any of them either way.
// `cq/components/partial/styles/ItemsList.module.scss` emits the same prefix, so this holds only
// while that module defines no `item` class of its own -- it defines `data-view*` names alone.
//
// A composite block -- menu test's stacked metric areas -- holds several chart+table pairs as bare
// siblings under one wrapper, and its tables number their rows from one apiece, so the wrapper-wide
// walk would pair a row with the first chart of a NEIGHBOURING area. Such a block declares each
// area's boundary with `data-echo-scope` (the EchoScope component beside BlockWrapper); the echo
// then never crosses it in either direction.
const BLOCK = '[data-echo-scope], [class*="ItemsList-module__item___"], [class*="BlockWrapper-module__container"]'

export const chartNear = el => {
  const container = el?.closest(BLOCK)?.querySelector('.highcharts-container')
  if (!container) return null
  return Highcharts.charts?.find(chart => chart?.container === container) || null
}

// A chart drawn as plain SVG rather than by Highcharts -- the polarity scale's poles -- cannot be
// reached through a chart object, so it names its own shapes with the id its table row carries and
// holds the echo ready beneath each one. The row only has to switch the right one on.
const POINT_LINKED = 'aytm-point-linked'

export const linkShapeForRow = (rowEl, id) => {
  const block = rowEl?.closest(BLOCK)
  if (!block) return
  for (const shape of block.querySelectorAll(`svg .${POINT_LINKED}`)) shape.classList.remove(POINT_LINKED)
  if (null == id) return
  block.querySelector(`svg [data-point-id="${id}"]`)?.classList.add(POINT_LINKED)
}

// `id` is the point the row answers to -- `a<position>` for an answer, `a<position>t<n>` for one
// trait value of it; null clears.
export const echoPointForRow = (rowEl, id) => {
  const chart = chartNear(rowEl)
  if (chart) return drawHalo(chart, id)

  linkShapeForRow(rowEl, id)
}

// The other direction: a column under the pointer tints its own row in the list below. No halo here
// -- the tooltip already names the point, so the chart side only has to say which row it is.
const ROW_LINKED = 'aytm-row-linked'

// A block can hold more than one table -- a trait split draws its own beneath the answers -- and
// they number their rows from one apiece, so the first match in the block is not necessarily the
// row the chart means. A nested table is drawn inside a row of the table above it, so the row that
// answers to the chart is the one with no row of its own above it.
const ROW = '[class*="Table-module__row___"]'

const outerRowAt = (block, position) => {
  for (const row of block.querySelectorAll(`[data-row-position="${position}"]`)) {
    if (!row.parentElement?.closest(ROW)) return row
  }
  return null
}

// an answer's point is named `a<position>`, the same position its table row carries; a trait row
// numbers the trait value instead, so it names its point outright rather than being found by one
const positionOfPoint = id => {
  const match = /^a(\d+)$/.exec(id || '')
  return match ? Number(match[1]) : null
}

const rowForPoint = (block, id) => {
  // scoped to rows: the shape a plain-SVG chart draws for this point carries the same id, and comes
  // first in the block
  const named = block.querySelector(`${ROW}[data-point-id="${id}"]`)
  if (named) return named

  const position = positionOfPoint(id)
  return null == position ? null : outerRowAt(block, position)
}

const linkRowInBlock = (block, id) => {
  if (!block) return
  for (const row of block.querySelectorAll(`.${ROW_LINKED}`)) row.classList.remove(ROW_LINKED)
  if (null == id) return
  rowForPoint(block, id)?.classList.add(ROW_LINKED)
}

export const linkRowFromPoint = (chart, id) => linkRowInBlock(chart?.container?.closest(BLOCK), id)

// the same direction from a plain-SVG chart, where the shape under the pointer stands in for the point
export const linkRowFromShape = (shapeEl, id) => linkRowInBlock(shapeEl?.closest(BLOCK), id)

// The same echo between a crossed pair's chart and its grid, where a cell rather than a row is what
// answers to a point. The two find each other by the id the block mints for them rather than by
// walking the DOM: one of the two hosts drawing the pair wraps them and the other mounts them as
// bare siblings, and a block whose chart is switched off would otherwise climb past its own markup
// and reach the next block down the page -- which names its points the same.
const CELL_LINKED = 'aytm-cell-linked'
const BLOCK_ATTR = 'data-cf-block'

const gridsOf = blockId => document.querySelectorAll(`[${BLOCK_ATTR}="${blockId}"]`)

export const linkCellFromPoint = (blockId, id) => {
  if (null == blockId) return
  for (const grid of gridsOf(blockId)) {
    for (const cell of grid.querySelectorAll(`.${CELL_LINKED}`)) cell.classList.remove(CELL_LINKED)
    if (null != id) grid.querySelector(`[data-cell-position="${id}"]`)?.classList.add(CELL_LINKED)
  }
}

export const echoPointForCell = (blockId, id) => {
  if (null == blockId) return
  const chart = Highcharts.charts?.find(c => c?.userOptions?.aytmCellBlock === blockId)
  if (chart) drawHalo(chart, id)
}
